import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState, AppDispatch } from '../store';
import { fetchRecentActivities } from '../store/activitySlice';
import { fetchDashboardStats } from '../store/dashboardSlice';
import { hasPermission, Permission } from '../constants/permissions';
import {
  ChartBarIcon,
  DocumentTextIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

const Dashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const { recentActivities: rawActivities, loading } = useSelector((state: RootState) => state.activities);
  const { stats: dashboardStats, loading: statsLoading } = useSelector((state: RootState) => state.dashboard);

  // Ensure recentActivities is always an array
  const recentActivities = Array.isArray(rawActivities) ? rawActivities : [];

  // Check permission
  if (!hasPermission(user, Permission.DASHBOARD_VIEW)) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don't have permission to view the dashboard.</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    dispatch(fetchRecentActivities(10));
    dispatch(fetchDashboardStats());
  }, [dispatch]);

  // Format currency in INR
  const formatCurrency = (value: number) => {
    if (value >= 10000000) { // 1 Crore
      return `₹${(value / 10000000).toFixed(2)}Cr`;
    } else if (value >= 100000) { // 1 Lakh
      return `₹${(value / 100000).toFixed(2)}L`;
    } else if (value >= 1000) {
      return `₹${(value / 1000).toFixed(1)}K`;
    }
    return `₹${value.toFixed(0)}`;
  };

  const stats = dashboardStats &&
    dashboardStats.totalPOs &&
    dashboardStats.pendingOrders &&
    dashboardStats.completedOrders &&
    dashboardStats.totalValue ? [
    {
      name: 'Total POs',
      value: dashboardStats.totalPOs.value?.toString() || '0',
      icon: DocumentTextIcon,
      color: 'bg-blue-500',
      change: `${dashboardStats.totalPOs.change >= 0 ? '+' : ''}${dashboardStats.totalPOs.change || 0}%`,
      changeType: dashboardStats.totalPOs.changeType || 'increase',
    },
    {
      name: 'Pending Orders',
      value: dashboardStats.pendingOrders.value?.toString() || '0',
      icon: ClockIcon,
      color: 'bg-yellow-500',
      change: `${dashboardStats.pendingOrders.change >= 0 ? '+' : ''}${dashboardStats.pendingOrders.change || 0}%`,
      changeType: dashboardStats.pendingOrders.changeType || 'increase',
    },
    {
      name: 'Completed',
      value: dashboardStats.completedOrders.value?.toString() || '0',
      icon: CheckCircleIcon,
      color: 'bg-green-500',
      change: `${dashboardStats.completedOrders.change >= 0 ? '+' : ''}${dashboardStats.completedOrders.change || 0}%`,
      changeType: dashboardStats.completedOrders.changeType || 'increase',
    },
    {
      name: 'Total Value',
      value: formatCurrency(dashboardStats.totalValue.value || 0),
      icon: ChartBarIcon,
      color: 'bg-purple-500',
      change: `${dashboardStats.totalValue.change >= 0 ? '+' : ''}${dashboardStats.totalValue.change || 0}%`,
      changeType: dashboardStats.totalValue.changeType || 'increase',
    },
  ] : [];

  const getActivityIcon = (type: string) => {
    const icons: Record<string, string> = {
      PO_CREATED: '📋',
      PO_ACCEPTED: '✅',
      PO_REJECTED: '❌',
      USER_CREATED: '👤',
      INVOICE_CREATED: '🧾',
      DISPATCH_CREATED: '📦',
      GRN_CREATED: '📥',
      QC_CREATED: '🔍',
    };
    return icons[type] || '📌';
  };

  const getActivityColor = (type: string) => {
    const colors: Record<string, string> = {
      PO_CREATED: 'bg-blue-100 text-blue-700',
      PO_ACCEPTED: 'bg-green-100 text-green-700',
      PO_REJECTED: 'bg-red-100 text-red-700',
      USER_CREATED: 'bg-purple-100 text-purple-700',
      INVOICE_CREATED: 'bg-yellow-100 text-yellow-700',
      DISPATCH_CREATED: 'bg-indigo-100 text-indigo-700',
      GRN_CREATED: 'bg-teal-100 text-teal-700',
      QC_CREATED: 'bg-orange-100 text-orange-700',
    };
    return colors[type] || 'bg-gray-100 text-gray-700';
  };

  const formatTimeAgo = (timestamp: string) => {
    const now = new Date();
    const past = new Date(timestamp);
    const diffInSeconds = Math.floor((now.getTime() - past.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    return past.toLocaleDateString();
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">
          Welcome back, {user?.firstName}!
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          Here's what's happening with your procurement today.
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {statsLoading ? (
          // Loading skeleton
          Array.from({ length: 4 }).map((_, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 animate-pulse"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                  <div className="h-8 bg-gray-300 rounded w-16 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                </div>
                <div className="bg-gray-200 rounded-lg p-3 h-14 w-14"></div>
              </div>
            </div>
          ))
        ) : (
          stats.map((stat) => (
            <div
              key={stat.name}
              className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">{stat.value}</p>
                  <div className="mt-2 flex items-center">
                    {stat.changeType === 'increase' ? (
                      <ArrowTrendingUpIcon className="h-4 w-4 text-green-600 mr-1" />
                    ) : (
                      <ArrowTrendingDownIcon className="h-4 w-4 text-red-600 mr-1" />
                    )}
                    <p className={`text-sm ${
                      stat.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {stat.change} from last month
                    </p>
                  </div>
                </div>
                <div className={`${stat.color} rounded-lg p-3`}>
                  <stat.icon className="h-8 w-8 text-white" />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Activity</h3>
        </div>
        
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : recentActivities.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <p className="text-gray-500">No recent activities</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {recentActivities.map((activity: any) => (
              <div
                key={activity._id}
                className="px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => navigate(`/activities/${activity._id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <span className="text-2xl">{getActivityIcon(activity.type)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {activity.description}
                      </p>
                      {activity.userId && (
                        <p className="text-sm text-gray-500 mt-1">
                          by {activity.userId.firstName} {activity.userId.lastName}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {formatTimeAgo(activity.timestamp)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getActivityColor(activity.type)}`}>
                      {activity.type.replace(/_/g, ' ')}
                    </span>
                    <ArrowRightIcon className="h-5 w-5 text-gray-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;